import { Card, Descriptions, Spin, Tag } from 'antd'
import { useEffect, useState, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { getShowLocationCoordinatesByIdAPI } from '@/apis/showLocation'
import { useTranslation } from 'react-i18next'

// 颜色常量
const COLOR_SHOW = '#ff9800'
const COLOR_START = '#4caf50'
const COLOR_WATCH = '#e91e63'
const TDT_KEY = '0c4056eff0604838856e8f72617bada0'


const ShowLocationDetail = () => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const id = searchParams.get('id')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const mapRef = useRef(null) // DOM 容器
    const mapObjRef = useRef(null) // 地图实例

    // 动态加载天地图脚本
    function ensureTiandituScript() {
        return new Promise(resolve => {
            if (window.T || window.TMap || window.Tianditu) {
                resolve()
                return
            }
            const existed = document.getElementById('tianditu_jsapi')
            if (existed) {
                existed.addEventListener('load', () => resolve())
                return
            }
            const script = document.createElement('script')
            script.id = 'tianditu_jsapi'
            script.type = 'text/javascript'
            // 卫星图 + 标注, v4 API
            script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TDT_KEY}`
            script.onload = () => resolve()
            document.body.appendChild(script)
        })
    }

    // 初始化并绘制多边形
    function renderMap(d) {
        if (!window.T || !mapRef.current) return
        const T = window.T
        if (!mapObjRef.current) {
                    mapObjRef.current = new T.Map(mapRef.current)
                    // 设置卫星图层（不同版本常量可能不同，尝试设置）
                            try {
                                if (T && T.MAP_SATELLITE) {
                                    mapObjRef.current.setMapType(T.MAP_SATELLITE)
                                }
                            } catch(e) { /* ignore */ }
        }
        const map = mapObjRef.current
        map.clearOverLays && map.clearOverLays()

        const polys = []
        const collections = [
            { key: 'show_area', color: COLOR_SHOW },
            { key: 'start_area', color: COLOR_START },
            { key: 'watch_area', color: COLOR_WATCH }
        ]
        collections.forEach(cfg => {
            const arr = Array.isArray(d?.[cfg.key]) ? d[cfg.key] : []
            if (arr.length >= 3) {
                const points = arr.map(p => new T.LngLat(p[0], p[1]))
                const polygon = new T.Polygon(points, {
                    color: cfg.color,
                    weight: 2,
                    opacity: 1,
                    fillColor: cfg.color,
                    fillOpacity: 0.25
                })
                map.addOverLay(polygon)
                polys.push(...points)
            } else if (arr.length === 1) {
                const pt = new T.LngLat(arr[0][0], arr[0][1])
                const marker = new T.Marker(pt)
                map.addOverLay(marker)
                polys.push(pt)
            }
        })
        // 自适应视野
                if (polys.length > 0) {
            let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity
            polys.forEach(p => {
                const lon = p.getLng(), lat = p.getLat()
                if (lon < minLon) minLon = lon
                if (lon > maxLon) maxLon = lon
                if (lat < minLat) minLat = lat
                if (lat > maxLat) maxLat = lat
            })
            if (isFinite(minLon) && isFinite(maxLon) && isFinite(minLat) && isFinite(maxLat)) {
                const centerLon = (minLon + maxLon) / 2
                const centerLat = (minLat + maxLat) / 2
                map.centerAndZoom(new T.LngLat(centerLon, centerLat), 12)
                // 根据跨度调整粗略 zoom
                const lonSpan = Math.max(0.0001, maxLon - minLon)
                const latSpan = Math.max(0.0001, maxLat - minLat)
                const span = Math.max(lonSpan, latSpan)
                // 简单经验映射：跨度越大，zoom 越小
                let zoom
                if (span > 5) zoom = 6
                else if (span > 1) zoom = 9
                else if (span > 0.2) zoom = 11
                else if (span > 0.05) zoom = 13
                else if (span > 0.01) zoom = 15
                else zoom = 16
                try { map.setZoom(zoom) } catch(e) { /* ignore */ }
            }
        }
    }

    useEffect(() => {
        async function fetchDetail() {
            if (!id) return
            setLoading(true)
            try {
                await ensureTiandituScript()
                const res = await getShowLocationCoordinatesByIdAPI(id)
                const detail = res?.data || null
                setData(detail)
                // 延迟等待容器渲染
                setTimeout(() => renderMap(detail), 50)
            } catch (e) {
                console.error('load showLocation detail failed:', e)
            } finally {
                setLoading(false)
            }
        }
        fetchDetail()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    return (
        <div style={{ padding: 16 }}>
            <Spin spinning={loading} size='large'>
                {data && (
                    <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                        <Card style={{ flex: '1 1 420px', minWidth: 360 }} title={data.placeName || 'Detail'}>
                            <Descriptions column={1} size='small' bordered>
                                <Descriptions.Item label={t('ShowLocation.FormList.PlaceName')}>{data.placeName}</Descriptions.Item>
                                <Descriptions.Item label={t('ShowLocation.FormList.Adress')}>{data.address}</Descriptions.Item>
                                <Descriptions.Item label={t('ShowLocation.FormList.WindLevel')}>{data.windLevel}</Descriptions.Item>
                                <Descriptions.Item label={t('ShowLocation.FormList.GeomagneticDetection')}>{data.geomagneticDetection}</Descriptions.Item>
                                <Descriptions.Item label={t('ShowLocation.FormList.FlyableAmount')}>{data.uavMin}-{data.uavMax}</Descriptions.Item>
                                <Descriptions.Item label={t('ShowLocation.FormList.Info')}>{data.info || '-'}</Descriptions.Item>
                                <Descriptions.Item label='City'>{data.city}</Descriptions.Item>
                                <Descriptions.Item label='Visible'>{data.visible === 1 ? <Tag color='green'>{t('Visible')||'Yes'}</Tag> : <Tag color='red'>No</Tag>}</Descriptions.Item>
                            </Descriptions>
                            <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
                                <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 16 }}><span style={{ width:12,height:12,background:COLOR_SHOW,display:'inline-block',marginRight:4 }} />Show</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', marginRight: 16 }}><span style={{ width:12,height:12,background:COLOR_START,display:'inline-block',marginRight:4 }} />Start</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center' }}><span style={{ width:12,height:12,background:COLOR_WATCH,display:'inline-block',marginRight:4 }} />Watch</span>
                            </div>
                        </Card>
                        <Card style={{ flex: '2 1 600px', minWidth: 480 }} title={t('ShowLocation.Map') || 'Map'}>
                            <div ref={mapRef} style={{ width: '100%', height: 500, borderRadius: 8, overflow: 'hidden', background: '#000' }} />
                        </Card>
                    </div>
                )}
            </Spin>
        </div>
    )
}

export default ShowLocationDetail